<template>
  <model-form
    ref="modelForm"
    :model="membershipDiscount"
    :title="`${membershipDiscount.membershipTypeId ? `Edit ${membershipDiscount.name} for ${membershipType.name}` : `Add Discount to ${membershipType.name}`}`"
    :submodel="true"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-col-gutter-x-sm items-center q-mb-md">
        <q-input
          v-model="membershipDiscount.name"
          class="col-9"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-toggle
          v-model="membershipDiscount.enable"
          class="col-3 q-pb-md"
          label="Enable"
          bottom-slots
        >
          <q-tooltip>
            Disable this discount to prevent redemption by members.
          </q-tooltip>
        </q-toggle>
        <q-select
          v-model="membershipDiscount.discount"
          class="col-12"
          label="Discount"
          :options="$_.compact($_.xorBy(discounts.data, $_.map(membershipType.membershipDiscounts, 'discount'), 'id'))"
          option-label="name"
          emit-value
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          @update:model-value="input => { if (!membershipDiscount.name) { membershipDiscount.name = input.name; } }"
        />
      </div>
      <div
        class="row q-col-gutter-x-sm items-center"
      >
        <q-toggle
          v-model="membershipDiscount.limitRedemptions"
          class="col-6 q-pb-md"
          label="Limit Redemptions"
          bottom-slots
        >
          <q-tooltip>
            Limit how many discounts a member can redeem within a given period.
            <br>The member's redemption period will begin with their membership term.
          </q-tooltip>
        </q-toggle>
        <q-input
          v-model.number="membershipDiscount.redemptionsLimit"
          :disable="!membershipDiscount.limitRedemptions"
          class="col-6"
          label="Redemptions Limit"
          type="number"
          step="1"
          min="1"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            The number of discounts redeemable by each member in the given period.
          </q-tooltip>
        </q-input>
        <q-input
          v-model.number="membershipDiscount.renews"
          :disable="!membershipDiscount.limitRedemptions"
          class="col-6"
          label="Renew Every"
          type="number"
          step="1"
          min="1"
          :max="maximumRenews"
          :rules="[val => !!val && val > 0 && val <= maximumRenews || ('Field cannot exceed ' + maximumRenews)]"
          bottom-slots
        >
          <q-tooltip>
            How often the member's discount redemptions should be reset, multiplied by the given term.
            <br>The membership term's latest start date is used to calculate this period.
          </q-tooltip>
        </q-input>
        <q-select
          v-model="membershipDiscount.renewalTerm"
          :disable="!membershipDiscount.limitRedemptions"
          class="col-6 text-capitalize"
          label="Renewal Term"
          :options="$mh.asOptions(MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.ALL)"
          emit-value
          map-options
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            The unit of time used for renewing the member's discount redemptions.
          </q-tooltip>
        </q-select>
        <model-form-select
          field="conditions"
          class="col-12 q-mt-md"
          label="Conditions"
          :options="$mh.asOptions(MEMBERSHIP_DISCOUNT_CONDITIONS.ALL)"
          map-options
          emit-value
          multiple
          use-chips
          clearable
          :decode="conditions => $mh.objectToArray(conditions)"
          :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: MEMBERSHIP_DISCOUNT_CONDITIONS.ALL })"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ MEMBERSHIP_DISCOUNT_CONDITIONS_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Set conditions to be met for members to redeem this discount. If multiple conditions are selected, all must be met for redemption.
          </q-tooltip>
        </model-form-select>
        <!-- Condition Requirements -->
        <date-range-condition-form
          v-if="membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DATE_RANGE]"
          :conditional="membershipDiscount"
          :date-range-start-field="CONDITION_REQUIREMENTS.DATE_RANGE_START"
          :date-range-end-field="CONDITION_REQUIREMENTS.DATE_RANGE_END"
          :exclude-date-range-field="CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE"
        />

        <day-of-week-condition-form
          v-if="membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.DAY_OF_WEEK]"
          :conditional="membershipDiscount"
          :day-of-week-field="CONDITION_REQUIREMENTS.DAY_OF_WEEK"
          :exclude-day-of-week-field="CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK"
        />

        <longevity-condition-form
          v-if="membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.LONGEVITY]"
          :conditional="membershipDiscount"
        />

        <sites-condition-form
          v-if="membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.SITES]"
          :conditional="membershipDiscount"
        />

        <tier-condition-form
          v-if="membershipDiscount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER]"
          :conditional="membershipDiscount"
          :membership-tiers="membershipType.membershipTiers.data"
        />
      </div>
    </div>
  </model-form>
</template>

<script>
import { CONDITION_REQUIREMENTS, MEMBERSHIP_DISCOUNT_CONDITIONS, MEMBERSHIP_DISCOUNT_RENEWAL_TERMS } from 'src/models/membership-type/MembershipDiscount'
import MembershipType from 'src/models/membership-type/MembershipType'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import DayOfWeekConditionForm from 'src/modules/admin/components/condition-requirement-forms/DayOfWeekConditionForm'
import LongevityConditionForm from 'src/modules/admin/components/condition-requirement-forms/LongevityConditionForm'
import SitesConditionForm from 'src/modules/admin/components/condition-requirement-forms/SitesConditionForm'
import TierConditionForm from 'src/modules/admin/components/condition-requirement-forms/TierConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const MEMBERSHIP_DISCOUNT_CONDITIONS_TOOLTIPS = {
  tier: 'Discounts will be redeemable by active members of the given tier.',
  'day-of-week': 'Discounts will be redeemable by active members on the given day of the week.',
  birthday: 'Discounts will be redeemable by active members on their birthday.',
  birthweek: 'Discounts will be redeemable by active members during their birthweek.',
  'date-range': 'Discounts will be redeemable by active members during a given date range.',
  longevity: 'Discounts will be redeemable by active members who have maintained their membership for the given interval.',
  sites: 'Discounts will be redeemable on orders for the given sites.'
}

export default {
  name: 'MembershipDiscountForm',
  components: {
    DateRangeConditionForm,
    DayOfWeekConditionForm,
    LongevityConditionForm,
    ModelForm,
    SitesConditionForm,
    TierConditionForm
  },
  configuration: ['discounts'],
  props: {
    membershipType: {
      type: MembershipType,
      required: true
    }
  },
  models: {
    membershipDiscount: {
      membershipType: 'default',
      discount: 'default',
      membershipTierIds: 'default'
    }
  },
  computed: {
    maximumRenews () {
      return this.membershipDiscount.renewalTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.YEARS ? 5 : this.membershipDiscount.renewalTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.MONTHS ? (5 * 12) : this.membershipDiscount.renewalTerm === MEMBERSHIP_DISCOUNT_RENEWAL_TERMS.WEEKS ? (5 * 52) : (5 * 365)
    }
  },
  created () {
    _.extend(this, { CONDITION_REQUIREMENTS, MEMBERSHIP_DISCOUNT_RENEWAL_TERMS, MEMBERSHIP_DISCOUNT_CONDITIONS, MEMBERSHIP_DISCOUNT_CONDITIONS_TOOLTIPS })
  }
}

</script>
